import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { ProgressSpinner } from "primereact/progressspinner";
import contractsAPI from "../../../services/contracts";
import trainingsAPI from "../../../services/trainings";

const PrintAllContractsPage = () => {
  const { schoolId, registrationId, trainingId } = useParams();
  const resolvedTrainingId = registrationId || trainingId;
  const [searchParams] = useSearchParams();
  const { accessToken } = useAuthContext();
  const [training, setTraining] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  const rawContractsFilter = searchParams.get("contractsFilter");
  let contractsFilter = {};

  try {
    contractsFilter = rawContractsFilter ? JSON.parse(rawContractsFilter) : {};
  } catch (error) {
    contractsFilter = {};
  }

  const contractsFilterForQuery = {
    name: contractsFilter?.name || "",
    not_fully_paid:
      contractsFilter?.not_fully_paid === "true"
        ? "true"
        : contractsFilter?.not_fully_paid === "fully_paid"
        ? "fully_paid"
        : "",
  };

  // Get contracts for this training
  const contractsQuery = contractsAPI.useGetList(
    schoolId,
    resolvedTrainingId,
    accessToken,
    1,
    1000,
    contractsFilterForQuery
  );

  // Get training data
  const trainingQuery = trainingsAPI.GetOne(schoolId, resolvedTrainingId, accessToken);
  const trainingData = trainingQuery?.data?.data;

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch training data
        const trainingResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/schools/${schoolId}/trainings/${resolvedTrainingId}`,
          {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + accessToken,
            },
          }
        );

        if (trainingResponse.ok) {
          const trainingData = await trainingResponse.json();
          setTraining(trainingData?.data);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [schoolId, resolvedTrainingId, accessToken]);

  useEffect(() => {
    if (contractsQuery.data?.data) {
      setContracts(contractsQuery.data.data);
    }
  }, [contractsQuery.data]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';

    // Handle DD/MM/YYYY format
    if (typeof dateString === 'string' && dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('fr-FR');
      }
    }

    // Fallback for other date formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('fr-FR');
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    return phone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
  };

  const formatAmount = (amount, currency = "€") => {
    return `${Number(amount || 0)} ${currency}`;
  };

  if (loading || contractsQuery.isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px'
      }}>
        Chargement...
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      margin: 0,
      padding: '20px',
      backgroundColor: 'white',
      minHeight: '100vh',
      background: 'white'
    }}>
      <style>
        {`
          html, body {
            background: white !important;
            background-color: white !important;
          }

          @media print {
            @page {
              size: A4 landscape;
              margin: 1cm;
            }
            html, body {
              margin: 0;
              padding: 0;
              background: white !important;
              background-color: white !important;
            }
            .page-break {
              page-break-before: always;
            }
            .no-print {
              display: none;
            }
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .contracts-table tr:nth-child(even) {
              background-color: #f9f9f9 !important;
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
              border-top: 1px solid #e0e0e0 !important;
              border-bottom: 1px solid #e0e0e0 !important;
            }
            .contracts-table tr:nth-child(odd) {
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
              border-top: 1px solid #f0f0f0 !important;
              border-bottom: 1px solid #f0f0f0 !important;
            }
          }

          .contracts-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 0;
            font-size: 12px;
          }

          .contracts-table th,
          .contracts-table td {
            border: 1px solid #333;
            padding: 8px;
            text-align: left;
            vertical-align: top;
          }

          .contracts-table th {
            background-color: #f5f5f5;
            font-weight: bold;
            text-align: center;
          }

          .contracts-table .no-wrap-cell {
            white-space: nowrap;
          }

          .contracts-table tr:nth-child(even) {
            background-color: #f9f9f9;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
            print-color-adjust: exact;
            border-top: 1px solid #e0e0e0;
            border-bottom: 1px solid #e0e0e0;
          }

          .contracts-table tr:nth-child(odd) {
            border-top: 1px solid #f0f0f0;
            border-bottom: 1px solid #f0f0f0;
          }

          .print-button {
            position: fixed;
            top: 90px;
            right: 20px;
            background-color: #007bff;
            color: white;
            border: none;
            padding: 15px 30px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 20px;
            font-weight: bold;
            z-index: 1000;
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2);
            transition: all 0.3s ease;
          }

          .print-button:hover {
            background-color: #0056b3;
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(0, 0, 0, 0.3);
          }

          .contracts-header {
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 20px;
            padding: 10px;
            background-color: #f8f9fa;
            border: 2px solid #333;
            text-align: center;
          }

          .training-header {
            text-align: center;
            margin-bottom: 30px;
            padding: 20px 0;
            border-bottom: 2px solid #333;
          }

          .training-header h1 {
            margin: 0 0 10px 0;
            font-size: 24px;
          }

          .training-header p {
            margin: 5px 0;
            font-size: 14px;
          }
        `}
      </style>

      <button
        className="print-button no-print"
        onClick={() => window.print()}
      >
        <i className="pi pi-print" style={{ marginRight: '8px' }}></i>
        Imprimer
      </button>

      <div className="training-header">
        <h1>Contrats</h1>
        <p><strong>{training?.name}</strong></p>
        <p>Date de génération : {new Date().toLocaleDateString('fr-FR')}</p>
      </div>

      {contracts.length > 0 ? (
        <>
          <table className="contracts-table">
            <thead>
              <tr>
                <th>Famille</th>
                <th>Élèves</th>
                <th>Parent/Tuteur</th>
                <th className="no-wrap-cell">Téléphone</th>
                <th>Email</th>
                <th className="no-wrap-cell">Total</th>
                <th className="no-wrap-cell">Reste à payer</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((contract) => (
                <tr key={contract.id}>
                  <td>{contract.name || '-'}</td>
                  <td>
                      {contract?.students?.length > 0 ? (
                        contract.students.map((student, index) => (
                          <React.Fragment key={student?.id || index}>
                            {`${student?.lastname || ""} ${student?.firstname || ""}`.trim() || "-"}
                            {index < contract.students.length - 1 && <br />}
                          </React.Fragment>
                        ))
                      ) : (
                        "-"
                      )}
                  </td>
                  <td>
                      {contract?.parents?.length > 0 ? (
                        contract.parents.map((parent, index) => (
                          <React.Fragment key={parent?.id || index}>
                            {`${parent?.lastname || ""} ${parent?.firstname || ""}`.trim() || "-"}
                            {index < contract.parents.length - 1 && <br />}
                          </React.Fragment>
                        ))
                      ) : (
                        "-"
                      )}
                  </td>
                  <td className="no-wrap-cell">
                      {contract?.parents?.length > 0 ? (
                        contract.parents.map((parent, index) => (
                          <React.Fragment key={parent?.id || index}>
                            {formatPhone(parent?.phone)}
                            {index < contract.parents.length - 1 && <br />}
                          </React.Fragment>
                        ))
                      ) : (
                        "-"
                      )}
                  </td>
                  <td>
                      {contract?.parents?.length > 0 ? (
                        contract.parents.map((parent, index) => (
                          <React.Fragment key={parent?.id || index}>
                            {parent?.email || "-"}
                            {index < contract.parents.length - 1 && <br />}
                          </React.Fragment>
                        ))
                      ) : (
                        "-"
                      )}
                  </td>
                  <td className="no-wrap-cell">{formatAmount(contract?.total_amount, contract?.currency)}</td>
                  <td className="no-wrap-cell">{formatAmount((contract?.total_amount || 0) - (contract?.paid_balance || 0), contract?.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: '30px', textAlign: 'center', fontSize: '12px', color: '#666' }}>
            <p>Total : {contracts.length} contrat(s)</p>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Aucun contrat trouvé dans cette formation</h2>
        </div>
      )}
    </div>
  );
};

export default PrintAllContractsPage;
